import React, { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Menu, Search, ShieldQuestion, LogOut, Loader2, CheckCircle2, ArrowRight, Lock, Sparkles, Feather, X } from "lucide-react";
import { Sidebar, NAV } from "@/components/portal/Sidebar";
import Gateway from "@/pages/Gateway";
import PublicFamilyTree from "@/pages/PublicFamilyTree";
import { api, getToken, clearToken } from "@/lib/api";

import Journey from "@/modules/Journey";
import Vault from "@/modules/Vault";
import RecordReader from "@/modules/RecordReader";
import AskRecords from "@/modules/AskRecords";
import FamilyTree from "@/modules/FamilyTree";
import TribalPaths from "@/modules/TribalPaths";
import LanguageLab from "@/modules/LanguageLab";
import HistoricalMap from "@/modules/HistoricalMap";
import Account from "@/modules/Account";
import Affiliation from "@/modules/Affiliation";
import RollSearch from "@/modules/RollSearch";
import TribalRecipes from "@/modules/TribalRecipes";
import TribalRemedies from "@/modules/TribalRemedies";
import CuisineMap from "@/modules/CuisineMap";
import Treaties from "@/modules/Treaties";
import AdminPortal from "@/modules/AdminPortal";

const MODULES = {
  journey: Journey,
  vault: Vault,
  record_reader: RecordReader,
  ask_records: AskRecords,
  family_tree: FamilyTree,
  tribal_paths: TribalPaths,
  language_lab: LanguageLab,
  historical_map: HistoricalMap,
  account: Account,
  tribal_affiliation: Affiliation,
  roll_search: RollSearch,
  tribal_recipes: TribalRecipes,
  tribal_remedies: TribalRemedies,
  cuisine_map: CuisineMap,
  treaties: Treaties,
  admin: AdminPortal,
};

const FREE_MODULES = new Set(["journey", "account"]);
const isMember = (u) => ["active", "trialing"].includes(u?.membership_status);
// Until a member generates their first Affiliation Report, only these tabs are accessible.
const AFFILIATION_UNLOCKED = new Set(["tribal_affiliation", "account"]);

const DisclaimerBar = () => (
  <div className="flex items-start gap-2.5 bg-[#2A1442] text-[#E9E2F2] px-4 sm:px-8 py-2.5 text-[11px] leading-relaxed">
    <ShieldQuestion size={15} className="text-[#E7C965] shrink-0 mt-0.5" />
    <span data-testid="portal-disclaimer">
      Uncover your authentic heritage, proclaim your Tribal Nationality, and receive your Complete Genealogy
      documentation package — professionally prepared for you with all of your official Paperwork, Tribal Enhanced
      IDs, Foreign National Passport, and More!{" "}
      <a href="https://ISelfLawAmMaster.com/product-page/" target="_blank" rel="noreferrer"
        className="text-[#E7C965] font-semibold underline underline-offset-2 hover:text-white transition-colors">
        Follow the Link for more info to get your FULL GENEALOGY PACKAGE →
      </a>
    </span>
  </div>
);

const PaymentReturn = ({ onDone, onCancel, kind }) => {
  const [status, setStatus] = useState(kind === "cancel" ? "canceled" : "checking");
  useEffect(() => {
    if (kind === "cancel") return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) { /* keep polling */ }
      if (++tries < 20) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
  }, [kind]);

  return (
    <div className="hp-parchment-texture min-h-screen grid place-items-center px-5">
      <div className="max-w-md w-full rounded-3xl bg-white border border-[#E4DCD3] p-9 text-center shadow-lg">
        {status === "checking" && (<>
          <Loader2 size={40} className="animate-spin text-[#59288E] mx-auto mb-5" />
          <h1 className="font-serif text-2xl font-bold text-[#180D24]">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-[#756D7E]">One moment while we finalize your Heritage Portal membership.</p>
        </>)}
        {status === "paid" && (<>
          <div className="h-16 w-16 rounded-full bg-[#ECFDF5] grid place-items-center mx-auto mb-5"><CheckCircle2 size={34} className="text-[#10B981]" /></div>
          <h1 className="font-serif text-3xl font-bold text-[#180D24]">Welcome to WhatsMyTribe!</h1>
          <p className="mt-2 text-[#4a4550]">Your membership is active. Your heritage journey begins now.</p>
          <button data-testid="enter-portal-btn" onClick={onDone} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-7 py-3.5 font-bold hover:bg-[#482276] transition-colors">
            Enter My Heritage Portal <ArrowRight size={18} />
          </button>
        </>)}
        {status === "canceled" && (<>
          <h1 className="font-serif text-2xl font-bold text-[#180D24]">Checkout canceled</h1>
          <p className="mt-2 text-sm text-[#756D7E]">No charge was made. You can start again whenever you're ready.</p>
          <button onClick={onCancel} className="mt-6 rounded-full bg-[#2A1442] text-white px-6 py-3 font-semibold hover:bg-[#482276]">Back to Start</button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <h1 className="font-serif text-2xl font-bold text-[#180D24]">Still processing</h1>
          <p className="mt-2 text-sm text-[#756D7E]">Your payment may take a moment. You can enter and we'll keep syncing your membership.</p>
          <button data-testid="enter-portal-btn" onClick={onDone} className="mt-6 rounded-full bg-[#2A1442] text-white px-6 py-3 font-semibold hover:bg-[#482276]">Continue</button>
        </>)}
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
};

const MembershipGate = ({ onStart, onAccount }) => (
  <div className="grid place-items-center py-16" data-testid="membership-gate">
    <div className="max-w-lg text-center rounded-3xl bg-white border border-[#E4DCD3] p-9 shadow-sm">
      <div className="h-16 w-16 rounded-2xl bg-[#F3EEFA] grid place-items-center mx-auto mb-5"><Lock size={30} className="text-[#59288E]" /></div>
      <h2 className="font-serif text-3xl font-bold text-[#180D24]">This is a member feature</h2>
      <p className="mt-3 text-[#4a4550]">
        Start your Heritage Portal membership to unlock your Ancestral Vault, AI Record Reader, Ask My Records,
        Family Tree, Language Lab, the Historical Map and more.
      </p>
      <div className="mt-5 inline-flex flex-col items-center gap-1 rounded-2xl bg-[#FFF9E6] border border-[#D4AF37]/40 px-6 py-4">
        <div className="flex items-center gap-2 text-[#8F7220]"><Sparkles size={16} /><span className="font-mono text-sm uppercase tracking-wider">Membership</span></div>
        <div className="font-serif text-2xl font-bold text-[#180D24]">$5<span className="text-base font-normal">/month</span></div>
        <div className="text-xs text-[#756D7E]">New students: $20 one-time research fee. Existing students: first 30 days free.</div>
      </div>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <button data-testid="gate-start-btn" onClick={onStart}
          className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 font-bold hover:bg-[#482276] transition-colors">
          Start My Membership <ArrowRight size={17} />
        </button>
        <button data-testid="gate-account-btn" onClick={onAccount}
          className="rounded-full border border-[#E4DCD3] px-6 py-3 font-semibold text-[#59288E] hover:border-[#D4AF37]">
          View Account
        </button>
      </div>
    </div>
  </div>
);


const AffiliationGate = ({ onStart }) => (
  <div className="grid place-items-center py-16" data-testid="affiliation-gate">
    <div className="max-w-lg text-center rounded-3xl bg-white border border-[#E4DCD3] p-9 shadow-sm">
      <div className="h-16 w-16 rounded-2xl bg-[#F3EEFA] grid place-items-center mx-auto mb-5"><Lock size={30} className="text-[#59288E]" /></div>
      <h2 className="font-serif text-3xl font-bold text-[#180D24]">Start with your Affiliation Report</h2>
      <p className="mt-3 text-[#4a4550]">
        Your heritage journey begins here. Generate your Tribal Affiliation Report first — it maps your maternal
        line to the tribes historically prevalent there and unlocks every other tool in your Heritage Portal.
      </p>
      <button data-testid="affiliation-gate-start" onClick={onStart}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-7 py-3.5 font-bold hover:bg-[#482276] transition-colors">
        <Feather size={17} /> Generate My Affiliation Report
      </button>
    </div>
  </div>
);

const WelcomeModal = ({ name, onStart }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm px-5" data-testid="welcome-modal">
    <div className="max-w-md w-full rounded-3xl bg-white p-8 text-center shadow-2xl relative">
      <div className="h-16 w-16 rounded-2xl bg-[#F3EEFA] grid place-items-center mx-auto mb-5"><Feather size={30} className="text-[#59288E]" /></div>
      <h2 className="font-serif text-3xl font-bold text-[#180D24]">Welcome{name ? `, ${name.split(" ")[0]}` : ""}!</h2>
      <p className="mt-3 text-[#4a4550]">
        To begin your heritage journey, please <span className="font-semibold">generate your Tribal Affiliation
        Report first</span>. Once it's ready, all of your other Heritage Portal tabs will unlock automatically.
      </p>
      <button data-testid="welcome-start-btn" onClick={onStart}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-7 py-3.5 font-bold hover:bg-[#E7C965] transition-colors">
        Start — Generate My Affiliation Report <ArrowRight size={18} />
      </button>
    </div>
  </div>
);


const PendingApproval = ({ user, onLogout }) => (
  <div className="min-h-screen grid place-items-center hp-parchment-texture px-5" data-testid="pending-approval">
    <div className="max-w-lg text-center rounded-3xl bg-white border border-[#E4DCD3] p-9 shadow-sm">
      <div className="h-16 w-16 rounded-2xl bg-[#FEF3C7] grid place-items-center mx-auto mb-5"><Lock size={30} className="text-[#B45309]" /></div>
      <h2 className="font-serif text-3xl font-bold text-[#180D24]">Your order is pending approval</h2>
      <p className="mt-3 text-[#4a4550]">
        Thank you{user?.name ? `, ${user.name.split(" ")[0]}` : ""}! We've received your submission
        {user?.order_number ? <> for order <span className="font-mono font-semibold">#{user.order_number}</span></> : null}.
        Our team is verifying your original order number before unlocking your free access.
        You'll be notified by email as soon as it's approved — this is usually quick.
      </p>
      <button onClick={onLogout} data-testid="pending-logout"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-6 py-3 font-bold hover:bg-[#482276] transition-colors">
        Log out
      </button>
    </div>
  </div>
);


function App() {
  const [active, setActive] = useState("journey");
  const [navOpen, setNavOpen] = useState(false);
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const [route, setRoute] = useState(path.startsWith("/payment/success") ? "pay_success" : path.startsWith("/payment/cancel") ? "pay_cancel" : "app");
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [user, setUser] = useState(null);
  const [view, setView] = useState("landing"); // landing | gateway
  const [gatewayScreen, setGatewayScreen] = useState("choose");
  const ActiveModule = MODULES[active] || Journey;
  const [navPayload, setNavPayload] = useState(null);
  const [welcomeSeen, setWelcomeSeen] = useState(false);

  const affiliationLocked = isMember(user) && user && !user.has_affiliation && user.role !== "admin";
  const selectTab = (id) => {
    if (affiliationLocked && !AFFILIATION_UNLOCKED.has(id)) {
      toast.info("Please generate your Affiliation Report first to unlock this feature.");
      setActive("tribal_affiliation");
      setNavPayload(null);
      return;
    }
    setActive(id);
  };
  const navigate = (id, payload) => {
    if (affiliationLocked && !AFFILIATION_UNLOCKED.has(id)) { selectTab(id); return; }
    setNavPayload(payload || null); setActive(id);
  };
  const refreshUser = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    api.get("/auth/me").then((r) => { setUser(r.data); setAuthed(true); }).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const goHome = () => { window.history.replaceState({}, "", "/"); setRoute("app"); };
  const enterPortal = () => {
    window.history.replaceState({}, "", "/"); setRoute("app"); setActive("journey"); window.scrollTo(0, 0);
    api.get("/auth/me").then((r) => { setUser(r.data); setAuthed(true); }).catch(() => setAuthed(true));
  };
  const openGateway = (screen = "choose") => { setGatewayScreen(screen); setView("gateway"); window.scrollTo(0, 0); };
  const onAuthed = (u) => { setUser(u); setAuthed(true); setActive("journey"); };
  const exit = () => { clearToken(); setUser(null); setAuthed(false); setView("landing"); setActive("journey"); };

  if (route === "pay_success") return <PaymentReturn kind="success" onDone={enterPortal} />;
  if (route === "pay_cancel") return <PaymentReturn kind="cancel" onCancel={goHome} />;
  if (path.startsWith("/tree/")) {
    const token = path.split("/tree/")[1]?.replace(/\/$/, "");
    return (<><PublicFamilyTree token={token} onHome={() => { window.location.href = "/"; }} /><Toaster position="top-right" richColors /></>);
  }

  if (authed === null) {
    return <div className="hp-parchment-texture min-h-screen grid place-items-center"><Loader2 size={26} className="animate-spin text-[#59288E]" /></div>;
  }

  if (!authed) {
    return (
      <>
        <Gateway initialScreen={gatewayScreen} onAuthed={onAuthed} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  if (user?.needs_approval) {
    return (
      <>
        <PendingApproval user={user} onLogout={exit} />
        <Toaster position="top-right" richColors />
      </>
    );
  }

  return (
    <div className="App flex hp-parchment-texture min-h-screen">
      <Sidebar active={active} onSelect={selectTab} open={navOpen} onClose={() => setNavOpen(false)}
        isAdmin={user?.role === "admin"}
        lockedIds={affiliationLocked ? new Set(NAV.map((n) => n.id).filter((id) => !AFFILIATION_UNLOCKED.has(id))) : null} />

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-md border-b border-[#E4DCD3]">
          <div className="flex items-center gap-3 px-4 sm:px-8 h-16">
            <button
              data-testid="mobile-nav-toggle"
              className="lg:hidden p-2 rounded-lg hover:bg-[#F3EEFA] text-[#2A1442]"
              onClick={() => setNavOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#756D7E]" />
              <input
                data-testid="global-search"
                placeholder="Search ancestors, records, citations…"
                className="w-full pl-9 pr-4 py-2 rounded-full bg-[#F4EFE6] border border-[#E4DCD3] text-sm
                  placeholder:text-[#9c93a5] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
              />
            </div>
            <div className="hidden sm:flex items-center gap-2 ml-auto">
              <div className="h-9 w-9 rounded-full bg-[#2A1442] grid place-items-center text-[#E7C965] font-serif font-bold">
                {(user?.name || "R").charAt(0).toUpperCase()}
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-[#180D24]">{user?.name || "Researcher"}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#8F7220]">
                  {isMember(user) ? (user.membership_status === "trialing" ? "Free Trial" : "Member") : "No Membership"}
                </div>
              </div>
              <button
                data-testid="exit-to-landing-btn"
                onClick={exit}
                title="Log out"
                className="ml-2 p-2 rounded-lg hover:bg-[#F3EEFA] text-[#59288E]"
              >
                <LogOut size={17} />
              </button>
            </div>
          </div>
          <DisclaimerBar />
        </header>

        <main className="flex-1 px-4 sm:px-8 py-6 sm:py-8 max-w-[1400px] w-full mx-auto">
          {!FREE_MODULES.has(active) && !isMember(user)
            ? <MembershipGate onStart={exit} onAccount={() => setActive("account")} />
            : (affiliationLocked && !AFFILIATION_UNLOCKED.has(active))
              ? <AffiliationGate onStart={() => setActive("tribal_affiliation")} />
              : <ActiveModule onNavigate={navigate} navPayload={navPayload} onAffiliationComplete={refreshUser} />}
        </main>
      </div>
      {affiliationLocked && !welcomeSeen && (
        <WelcomeModal name={user?.name} onStart={() => { setWelcomeSeen(true); setActive("tribal_affiliation"); }} />
      )}
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
